import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { FieldInfo } from '../../EmployeeData';
import { FormGroup } from '../../../../../components/form/FormGroup';
import InputLabel from '../../../../../components/form/InputLabel';
import { InfoHint } from '../../../../../components/hint/InfoHint';
import {
  ExtraFieldsConfigReferenceInput,
  useExtraFieldLookupOptionsQuery,
} from '../../../__generated__/graphql';
import { SelectDropdownControlled } from '../../../../../components/form/Selects';
import { ExtraFieldContextParam } from '../types';
import { useErrorHandler } from '../../../../../hooks/useErrorHandler';
import { createInternalLookupValue } from '../extraFieldInternalValue';

type Props = {
  fieldId: string;
  fieldInfo: FieldInfo;
  contextParams?: ExtraFieldContextParam[];
  disabled?: boolean;
  extraFieldsConfigReference?: ExtraFieldsConfigReferenceInput;
  hideWhenEmpty?: boolean;
};

export const ExtraFieldLookup: React.FC<React.PropsWithChildren<Props>> = ({
  fieldId,
  contextParams,
  fieldInfo,
  disabled,
  extraFieldsConfigReference,
  hideWhenEmpty = false,
}) => {
  const { data, loading, error } = useExtraFieldLookupOptionsQuery({
    variables: {
      id: fieldId,
      contextParams: contextParams ?? [],
      extraFieldsConfigReference: extraFieldsConfigReference ?? null,
    },
  });

  const addError = useErrorHandler();
  const { watch, setValue } = useFormContext();
  const currentValue = watch(fieldInfo.name);

  useEffect(() => {
    if (error) {
      addError(error);
    }
  }, [error, addError]);

  const options = useMemo(() => {
    if (!data?.extraFields.extraFieldLookupOptions.length) {
      return [];
    }
    return [
      ...data.extraFields.extraFieldLookupOptions.map((x) => ({
        value: createInternalLookupValue(x),
        label: x.label,
      })),
    ];
  }, [data]);

  useEffect(() => {
    if (
      !loading &&
      !error &&
      currentValue &&
      !options.some((o) => o.value === currentValue)
    ) {
      setValue(fieldInfo.name, null, true);
    }
  }, [options, loading, error, currentValue, fieldInfo.name, setValue]);

  if (hideWhenEmpty && (loading || options.length === 0) && !currentValue)
    return null;

  return (
    <FormGroup>
      <InputLabel
        label={fieldInfo.label}
        required={fieldInfo.required}
        hint={
          fieldInfo.hint ? (
            <InfoHint className="mt-2" text={fieldInfo.hint} />
          ) : undefined
        }
      />
      <SelectDropdownControlled
        isLoading={loading}
        isDisabled={disabled}
        name={fieldInfo.name}
        options={options}
        heightMode="large"
      />
    </FormGroup>
  );
};
